// Occurrence calculation library for recurring tasks
// Handles dynamic calculation of task occurrences based on recurrence rules
// CHANGE: Created for Phase 2 refactoring - Dynamic recurrence system
// UPDATED: Status values changed to not_started, completed, overdue
// UPDATED: Date utilities consolidated into dateUtils.ts - imported from there instead of local definitions
// CHANGE: Removed all debug console statements for production
// UPDATED: CalculatedOccurrence now carries frozen template metadata fields from occurrence snapshot columns
// UPDATED: mergeWithExceptions propagates frozenTemplate* fields from exception records

import { supabase } from './supabase';
import {
  formatDate,
  parseDate,
  addDays,
  addMonths,
  addYears,
  adjustDayForMonth,
  getDayOfWeek,
} from './dateUtils';
import type {
  AssignedTask,
  RecurrenceRule,
  AssignedTaskOccurrence,
  TaskTemplate
} from './supabase';
import { withSpan } from './sentry';

// Represents a calculated occurrence of a recurring task
export interface CalculatedOccurrence {
  date: string; // Format: YYYY-MM-DD
  scheduledTime: string | null; // Format: HH:MM
  duration: number | null; // In minutes
  assignedUserId: string; // User assigned to this occurrence
  status: 'not_started' | 'completed' | 'overdue';
  isCancelled: boolean;
  isModified: boolean; // Has an exception/override
  notes: string | null;
  occurrenceId: string | null; // ID if exception exists
  frozenTemplateName: string | null;
  frozenTemplateDescription: string | null;
  frozenTemplateEstimatedDurationMinutes: number | null;
  frozenTemplateServiceId: string | null;
}

// Represents a full task occurrence with all details
export interface TaskOccurrence {
  assignedTaskId: string;
  templateId: string | null;
  templateName: string;
  userId: string;
  siteId: string;
  occurrence: CalculatedOccurrence;
  isRecurring: boolean;
}

// Options for calculating occurrences
export interface CalculateOccurrencesOptions {
  startDate: Date;
  endDate: Date;
  includeExceptions?: boolean; // Include cancelled occurrences
}

// ============================================================================
// RECURRENCE CALCULATION
// ============================================================================

// Calculate if a date matches a recurrence rule (considering interval and start date)
export function matchesRecurrenceRule(date: Date, rule: RecurrenceRule, startDate?: Date): boolean {
  const dayOfWeek = getDayOfWeek(date);
  const dayOfMonth = date.getDate();
  const month = date.getMonth();
  const interval = rule.interval || 1;

  switch (rule.frequency) {
    case 'daily':
      // Check if the number of days from start date is divisible by interval
      if (startDate && interval > 1) {
        const daysDiff = Math.floor((date.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
        return daysDiff >= 0 && daysDiff % interval === 0;
      }
      return true;

    case 'weekly':
      // Two types of weekly recurrence:
      // 1. Simple weekly (no days_of_week) - repeats every 7 days from start date
      // 2. Custom weekly (with days_of_week) - repeats on specific days
      if (!rule.days_of_week || rule.days_of_week.length === 0) {
        // Simple weekly - check if same day of week as start date
        if (startDate) {
          const startDayOfWeek = getDayOfWeek(startDate);
          if (dayOfWeek !== startDayOfWeek) {
            return false;
          }
        }
      } else {
        // Custom weekly - check if day is in selected days
        if (!rule.days_of_week.includes(dayOfWeek)) {
          return false;
        }
      }

      // Check interval: number of weeks from start date
      if (startDate && interval > 1) {
        const daysDiff = Math.floor((date.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
        const weeksDiff = Math.floor(daysDiff / 7);
        return weeksDiff >= 0 && weeksDiff % interval === 0;
      }

      return true;

    case 'monthly':
      // Check interval: number of months from start date
      if (startDate && interval > 1) {
        const monthsDiff = (date.getFullYear() - startDate.getFullYear()) * 12 +
          (date.getMonth() - startDate.getMonth());
        if (monthsDiff < 0 || monthsDiff % interval !== 0) {
          return false;
        }
      }

      if (rule.day_of_month) {
        // Adjust for months with fewer days
        const adjustedDay = adjustDayForMonth(rule.day_of_month, month, date.getFullYear());
        return dayOfMonth === adjustedDay;
      }

      if (rule.week_of_month && rule.days_of_week.length > 0) {
        // Calculate which week of the month this is
        const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const firstDayWeekday = firstDayOfMonth.getDay();
        const weekOfMonth = Math.ceil((dayOfMonth + firstDayWeekday) / 7);

        // Special case: week_of_month = 5 means "last week"
        if (rule.week_of_month === 5) {
          const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const lastWeek = Math.ceil((lastDayOfMonth + firstDayWeekday) / 7);
          return weekOfMonth === lastWeek && rule.days_of_week.includes(dayOfWeek);
        }

        return weekOfMonth === rule.week_of_month && rule.days_of_week.includes(dayOfWeek);
      }
      return false;

    case 'yearly':
      // Check if month matches
      if (!rule.month_of_year || (month + 1) !== rule.month_of_year) {
        return false;
      }

      // Check interval: number of years from start date
      if (startDate && interval > 1) {
        const yearsDiff = date.getFullYear() - startDate.getFullYear();
        if (yearsDiff < 0 || yearsDiff % interval !== 0) {
          return false;
        }
      }

      // Check day pattern (either fixed day or week pattern)
      if (rule.day_of_month) {
        const adjustedDay = adjustDayForMonth(rule.day_of_month, month, date.getFullYear());
        return dayOfMonth === adjustedDay;
      }

      if (rule.week_of_month && rule.days_of_week.length > 0) {
        // Calculate which week of the month this is
        const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const firstDayWeekday = firstDayOfMonth.getDay();
        const weekOfMonth = Math.ceil((dayOfMonth + firstDayWeekday) / 7);

        // Special case: week_of_month = 5 means "last week"
        if (rule.week_of_month === 5) {
          const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const lastWeek = Math.ceil((lastDayOfMonth + firstDayWeekday) / 7);
          return weekOfMonth === lastWeek && rule.days_of_week.includes(dayOfWeek);
        }

        return weekOfMonth === rule.week_of_month && rule.days_of_week.includes(dayOfWeek);
      }

      return false;

    default:
      return false;
  }
}

// Calculate all occurrences for a recurring task in a date range
export function calculateOccurrences(
  task: AssignedTask,
  rule: RecurrenceRule,
  options: CalculateOccurrencesOptions
): CalculatedOccurrence[] {
  if (!task.is_recurring || !task.recurrence_start_date) {
    return [];
  }

  const occurrences: CalculatedOccurrence[] = [];
  const startDate = parseDate(task.recurrence_start_date);
  const endDate = task.recurrence_end_date ? parseDate(task.recurrence_end_date) : options.endDate;

  // Iterate through each day in the range
  let currentDate = new Date(Math.max(startDate.getTime(), options.startDate.getTime()));
  const finalDate = new Date(Math.min(endDate.getTime(), options.endDate.getTime()));

  while (currentDate <= finalDate) {
    if (matchesRecurrenceRule(currentDate, rule, startDate)) {
      occurrences.push({
        date: formatDate(currentDate),
        scheduledTime: task.scheduled_time,
        duration: task.duration_override,
        assignedUserId: task.assigned_to_user_id,
        status: 'not_started',
        isCancelled: false,
        isModified: false,
        notes: null,
        occurrenceId: null,
        frozenTemplateName: null,
        frozenTemplateDescription: null,
        frozenTemplateEstimatedDurationMinutes: null,
        frozenTemplateServiceId: null
      });
    }
    currentDate = addDays(currentDate, 1);
  }

  return occurrences;
}

// ============================================================================
// EXCEPTIONS MANAGEMENT
// ============================================================================

// Get all exceptions for a recurring task
export async function getOccurrenceExceptions(
  assignedTaskId: string,
  startDate?: Date,
  endDate?: Date
): Promise<AssignedTaskOccurrence[]> {
  try {
    const startFormatted = startDate ? formatDate(startDate) : null;
    const endFormatted = endDate ? formatDate(endDate) : null;

    let query = supabase
      .from('assigned_task_occurrences')
      .select('*')
      .eq('assigned_task_id', assignedTaskId)
      .order('occurrence_date', { ascending: true });

    if (startDate) {
      query = query.gte('occurrence_date', startFormatted);
    }
    if (endDate) {
      query = query.lte('occurrence_date', endFormatted);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    throw error;
  }
}

/**
 * OPTIMIZATION: Batch load all exceptions for multiple recurring tasks in a single query
 * Reduces N queries to 1 query using IN clause
 */
export async function batchLoadOccurrenceExceptions(
  assignedTaskIds: string[],
  startDate?: Date,
  endDate?: Date
): Promise<Map<string, AssignedTaskOccurrence[]>> {
  if (assignedTaskIds.length === 0) {
    return new Map();
  }

  try {
    const startFormatted = startDate ? formatDate(startDate) : null;
    const endFormatted = endDate ? formatDate(endDate) : null;

    let query = supabase
      .from('assigned_task_occurrences')
      .select('*')
      .in('assigned_task_id', assignedTaskIds)
      .order('assigned_task_id, occurrence_date', { ascending: true });

    if (startDate) {
      query = query.gte('occurrence_date', startFormatted);
    }
    if (endDate) {
      query = query.lte('occurrence_date', endFormatted);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Group exceptions by assigned_task_id
    const exceptionsMap = new Map<string, AssignedTaskOccurrence[]>();
    (data || []).forEach(exception => {
      const existing = exceptionsMap.get(exception.assigned_task_id) || [];
      existing.push(exception);
      exceptionsMap.set(exception.assigned_task_id, existing);
    });

    return exceptionsMap;
  } catch (error) {
    throw error;
  }
}

// Merge calculated occurrences with exceptions and apply reassignment rules
export function mergeWithExceptions(
  calculated: CalculatedOccurrence[],
  exceptions: AssignedTaskOccurrence[],
  task?: AssignedTask  // Add optional task parameter to check reassignment rule
): CalculatedOccurrence[] {
  // Create a map of exceptions by date for quick lookup
  const exceptionMap = new Map<string, AssignedTaskOccurrence>();
  exceptions.forEach(exc => {
    exceptionMap.set(exc.occurrence_date, exc);
  });

  // Merge calculated occurrences with exceptions
  const merged = calculated.map(calc => {
    const exception = exceptionMap.get(calc.date);

    // Start with calculated values
    let result = { ...calc };

    // Apply reassignment rule if applicable (before individual overrides)
    if (task?.reassignment_from_date && calc.date >= task.reassignment_from_date) {
      // Apply the reassignment rule ONLY if there's no individual override
      if (!exception?.assigned_to_user_id_override && task.reassignment_user_id) {
        result.assignedUserId = task.reassignment_user_id;
        result.isModified = true;
      }
      if (!exception?.scheduled_time_override && task.reassignment_scheduled_time) {
        result.scheduledTime = task.reassignment_scheduled_time;
        result.isModified = true;
      }
    }

    // Apply individual exception overrides (these take precedence over rule)
    if (exception) {
      result = {
        ...result,
        scheduledTime: exception.scheduled_time_override || result.scheduledTime,
        duration: exception.duration_override || result.duration,
        assignedUserId: exception.assigned_to_user_id_override || result.assignedUserId,
        status: exception.status !== null ? exception.status : result.status,
        isCancelled: exception.is_cancelled,
        isModified: true,
        notes: exception.notes,
        occurrenceId: exception.id,
        frozenTemplateName: exception.template_name ?? null,
        frozenTemplateDescription: exception.template_description ?? null,
        frozenTemplateEstimatedDurationMinutes: exception.template_estimated_duration_minutes ?? null,
        frozenTemplateServiceId: exception.template_service_id ?? null
      };
    }

    return result;
  });

  // Filter out cancelled occurrences
  return merged.filter(occ => !occ.isCancelled);
}

// ============================================================================
// HIGH-LEVEL API
// ============================================================================

// Get all occurrences for a recurring task (with exceptions applied)
export async function getTaskOccurrences(
  task: AssignedTask,
  rule: RecurrenceRule,
  options: CalculateOccurrencesOptions,
  preloadedExceptions?: AssignedTaskOccurrence[] // OPTIMIZATION: Accept preloaded exceptions
): Promise<CalculatedOccurrence[]> {
  return withSpan('occurrences.getTaskOccurrences', 'function', async () => {
    try {
      // Calculate base occurrences
      const calculated = calculateOccurrences(task, rule, options);

      // Get exceptions (use preloaded if available)
      const exceptions = preloadedExceptions !== undefined
        ? preloadedExceptions
        : await getOccurrenceExceptions(task.id, options.startDate, options.endDate);

      // Merge and return (pass task for reassignment rule)
      const merged = mergeWithExceptions(calculated, exceptions, task);

      return merged;
    } catch (error) {
      throw error;
    }
  });
}

// Get all task occurrences for multiple tasks
export async function getMultipleTaskOccurrences(
  tasks: Array<{ task: AssignedTask; rule: RecurrenceRule; template?: TaskTemplate }>,
  options: CalculateOccurrencesOptions
): Promise<TaskOccurrence[]> {
  try {
    const results: TaskOccurrence[] = [];

    for (const { task, rule, template } of tasks) {
      const occurrences = await getTaskOccurrences(task, rule, options);

      occurrences.forEach(occ => {
        results.push({
          assignedTaskId: task.id,
          templateId: task.template_id,
          templateName: template?.name || 'Mission',
          userId: task.assigned_to_user_id,
          siteId: task.site_id,
          occurrence: occ,
          isRecurring: true
        });
      });
    }

    return results;
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// OCCURRENCE MODIFICATIONS
// ============================================================================

// Create or update an exception for a specific occurrence
export async function modifyOccurrence(
  assignedTaskId: string,
  occurrenceDate: string,
  modifications: {
    scheduledTimeOverride?: string | null;
    durationOverride?: number | null;
    assignedToUserIdOverride?: string | null;
    status?: 'not_started' | 'completed' | 'overdue' | null;
    isCancelled?: boolean;
    notes?: string | null;
  }
): Promise<AssignedTaskOccurrence> {
  try {
    // Check if an exception already exists
    const { data: existing } = await supabase
      .from('assigned_task_occurrences')
      .select('*')
      .eq('assigned_task_id', assignedTaskId)
      .eq('occurrence_date', occurrenceDate)
      .maybeSingle();

    const modData = {
      assigned_task_id: assignedTaskId,
      occurrence_date: occurrenceDate,
      scheduled_time_override: modifications.scheduledTimeOverride,
      duration_override: modifications.durationOverride,
      assigned_to_user_id_override: modifications.assignedToUserIdOverride,
      status: modifications.status,
      is_cancelled: modifications.isCancelled ?? false,
      notes: modifications.notes
    };

    if (existing) {
      // Update existing exception
      const { data, error } = await supabase
        .from('assigned_task_occurrences')
        .update(modData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } else {
      // Create new exception
      const { data, error } = await supabase
        .from('assigned_task_occurrences')
        .insert(modData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    }
  } catch (error) {
    throw error;
  }
}

// Cancel a specific occurrence
export async function cancelOccurrence(
  assignedTaskId: string,
  occurrenceDate: string,
  notes?: string
): Promise<AssignedTaskOccurrence> {
  return modifyOccurrence(assignedTaskId, occurrenceDate, {
    isCancelled: true,
    notes: notes || 'Occurrence annulée'
  });
}

// Delete an exception (restore to default calculated occurrence)
export async function deleteOccurrenceException(occurrenceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('assigned_task_occurrences')
      .delete()
      .eq('id', occurrenceId);

    if (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// PLANNING HELPERS
// ============================================================================

// Get the default period for planning view (1 month)
export function getDefaultPlanningPeriod(): { startDate: Date; endDate: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = today;
  const endDate = addMonths(today, 1);

  return { startDate, endDate };
}

// Get extended period (up to 12 months)
export function getExtendedPlanningPeriod(months: number): { startDate: Date; endDate: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = today;
  const endDate = addMonths(today, Math.min(months, 12));

  return { startDate, endDate };
}

// Count occurrences for a recurring task in a period
export function countOccurrences(
  task: AssignedTask,
  rule: RecurrenceRule,
  startDate: Date,
  endDate: Date
): number {
  const occurrences = calculateOccurrences(task, rule, { startDate, endDate });
  return occurrences.length;
}

// ============================================================================
// NEXT OCCURRENCE CALCULATION
// ============================================================================

// Get the next occurrence date for a recurring task (excluding cancelled occurrences)
// Returns null if no future occurrence exists within the period
export function getNextOccurrenceForTask(
  task: AssignedTask,
  rule: RecurrenceRule,
  startDate: Date,
  endDate: Date,
  exceptions: AssignedTaskOccurrence[]
): string | null {
  // For non-recurring tasks, return the assigned date if it's in the future
  if (!task.is_recurring || !task.recurrence_start_date) {
    const assignedDate = parseDate(task.assigned_date);
    return assignedDate >= startDate ? task.assigned_date : null;
  }

  // Calculate all occurrences for the period
  const calculated = calculateOccurrences(task, rule, { startDate, endDate });

  // Create a set of cancelled dates for quick lookup
  const cancelledDates = new Set(
    exceptions
      .filter(exc => exc.is_cancelled)
      .map(exc => exc.occurrence_date)
  );

  // Find the first non-cancelled occurrence
  for (const occurrence of calculated) {
    if (!cancelledDates.has(occurrence.date)) {
      return occurrence.date;
    }
  }

  // No future occurrence found
  return null;
}
